import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useMemo, useState } from 'react';
import { ChatDialogue, ChatDialogueProps } from './ChatDialogue';
import MediaPreview from '../MediaPreview/MediaPreview';
import { ChatMessageEditor } from './ChatMessageEditor';
import { Tabs } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { ChannelIcon } from 'views/Conversation/ConvList/ChannelIcon';

type TargetType = 'buyer' | 'mercado';

interface MercadoClaimChatProps extends ChatDialogueProps {
  chatAlert: JSX.Element;
  contextMenu: JSX.Element | null;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
}

const messageTabKey = 'message';

export const MercadoClaimChat = (props: MercadoClaimChatProps) => {
  const { chatAlert, contextMenu, conversation, forceAcquireControlReason, ...chatDialogueProps } =
    props;
  const { mercadoCapability } = conversation;

  const { t } = useTranslate();

  const focusTarget = useMemo(() => {
    return mercadoCapability?.results.find(
      ({ focus }) => focus === 'MERCADO_FOCUS_BUYER' || focus === 'MERCADO_FOCUS_MEDIATION',
    );
  }, [mercadoCapability]);

  const [activeTabKey, setActiveTabKey] = useState(
    () => focusTarget?.resources.claimId || messageTabKey,
  );

  const [targetType, setTargetType] = useState<TargetType>(() =>
    focusTarget?.resources.claimId && focusTarget.focus === 'MERCADO_FOCUS_MEDIATION'
      ? 'mercado'
      : 'buyer',
  );

  const onMercadoChatView = targetType === 'mercado';

  const { mediation, hadMediation, canBeSent } = useMemo(() => {
    if (activeTabKey === messageTabKey) {
      return conversation;
    }
    const target = conversation.mercadoCapability?.results.find(
      ({ resources }) => resources.claimId === activeTabKey,
    );
    return (
      target || {
        mediation: false,
        hadMediation: false,
        canBeSent: false,
      }
    );
  }, [conversation, activeTabKey]);

  const forceDisableText = useMemo(() => {
    if (mediation && !onMercadoChatView) {
      return t('scene.chatWorkspace.messageThread.mercadoMediation.status.mercadoMediating');
    }
    if (!mediation && hadMediation && onMercadoChatView) {
      return t('scene.chatWorkspace.messageThread.mercadoMediation.status.disputeEnded');
    }
    return undefined;
  }, [mediation, hadMediation, onMercadoChatView, t]);

  return (
    <Container>
      <Tabs
        size="small"
        activeKey={activeTabKey}
        onChange={(key) => {
          setActiveTabKey(key);
          if (key === messageTabKey) {
            setTargetType('buyer');
          } else {
            const target = mercadoCapability?.results.find(
              ({ resources }) => resources.claimId === key,
            );
            if (target) {
              setTargetType(
                target.focus === 'MERCADO_FOCUS_MEDIATION'
                  ? 'mercado'
                  : target.focus === 'MERCADO_FOCUS_BUYER'
                  ? 'buyer'
                  : target.mediation
                  ? 'mercado'
                  : 'buyer',
              );
            } else {
              setTargetType('buyer');
            }
          }
        }}
      >
        <Tabs.TabPane key={messageTabKey} tab={t('nav.conversation')} />
        {mercadoCapability?.results.map(({ resources: { claimId } }) => {
          if (!claimId) {
            return null;
          }
          return <Tabs.TabPane key={claimId} tab={`${t('trade.争议')}#${claimId.slice(-4)}`} />;
        })}
      </Tabs>
      {hadMediation && activeTabKey !== messageTabKey && (
        <span>
          {t('对话')}:
          <div
            className={targetType === 'buyer' ? 'active' : undefined}
            onClick={() => setTargetType('buyer')}
          >
            <UserOutlined />
            {t('tab.buyer')}
          </div>
          <div
            className={targetType === 'mercado' ? 'active' : undefined}
            onClick={() => setTargetType('mercado')}
          >
            <ChannelIcon className="w-3 h-3" channel="MERCADO" />
            Mercado
          </div>
        </span>
      )}
      <section key={`${activeTabKey}@@${onMercadoChatView}`}>
        {chatAlert}
        <ChatDialogue
          conversation={conversation}
          onMercadoChatView={onMercadoChatView}
          claimId={activeTabKey === messageTabKey ? undefined : activeTabKey}
          {...chatDialogueProps}
        />
        <ChatMessageEditor
          conversation={conversation}
          forceAcquireControlReason={forceAcquireControlReason}
          showSideFoldBtn={false}
          onMercadoChatView={onMercadoChatView}
          claimId={activeTabKey === messageTabKey ? undefined : activeTabKey}
          canBeSent={canBeSent}
          forceDisableText={forceDisableText}
        />
        {contextMenu}
        <MediaPreview />
      </section>
    </Container>
  );
};

const Container = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  > .ant-tabs {
    flex: 0 0 auto;
    > .ant-tabs-nav {
      padding-left: 12px;
      margin-bottom: 1px;
      box-shadow: 0 0 1px 0px rgba(0, 0, 0, 0.2);
    }
  }
  > span {
    display: inline-flex;
    align-items: center;
    position: absolute;
    top: 48px;
    right: 14px;
    z-index: 2;
    padding: 2px;
    padding-left: 8px;
    background: var(--fett-surface-warning-bg);
    border-radius: 8px;
    border: solid 1px #ececec;
    box-shadow: 0px 2px 8px 0px #a7b2cc29;
    > div {
      margin-left: 4px;
      padding: 2px 8px;
      border-radius: 8px;
      cursor: pointer;
      > * {
        margin-right: 4px;
      }
    }
    > .active {
      background: var(--fett-surface-warning-border);
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
  }
`;
